import { useCallback, useMemo, useState } from 'react';
import type { MouseEvent, ReactNode } from 'react';
import { MoreVert, PictureAsPdfOutlined, PlaylistAddCheckOutlined, TableChartOutlined } from '@mui/icons-material';
import { BULK_ACTIONS, EXCEL_EXPORT, PDF_EXPORT } from 'common/constants/pageActions';
import { useExtraPageActionVisibility } from 'common/hooks';
import { PdfExportConfigDialog } from 'components/PdfExportConfigDialog';
import { PrimaryNavigationMenu, PrimaryNavigationMenuItem } from 'components/ui';
import type { SecondaryNavigationIconButtonControlConfig } from 'context/FirmBaseLayoutContext';
import BulkActionsDrawer from 'layouts/Main/components/BulkActionsLedger/BulkActionsDrawer';
import { FirmExcelExportDialog } from 'layouts/Main/components/ExcelExportDialogV2/FirmExcelExportDialog';
import { FundExcelExportDialog } from 'layouts/Main/components/ExcelExportDialogV2/FundExcelExportDialog';
import { useUserUtils } from 'utilities/userUtils';
import { useIntelligenceContext } from './IntelligenceContext';

const MORE_VERT_ICON = <MoreVert />;

export interface UseIntelligenceExportActionsResult {
  secondaryNavigationActions: SecondaryNavigationIconButtonControlConfig[];
  exportDialogs: ReactNode;
  moreActionsMenu: ReactNode;
}

export const useIntelligenceExportActions = (): UseIntelligenceExportActionsResult => {
  const { isAllFundsSelected, selectedFundId } = useIntelligenceContext();
  const { isStaffUser, isSuperUser } = useUserUtils();
  const actionVisibility = useExtraPageActionVisibility({
    pdfExport: isStaffUser() || isSuperUser(),
  });

  const [moreActionsAnchorEl, setMoreActionsAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [excelExportOpen, setExcelExportOpen] = useState(false);
  const [bulkActionsOpen, setBulkActionsOpen] = useState(false);
  const [pdfExportOpen, setPdfExportOpen] = useState(false);

  const handleMoreActionsOpen = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    setMoreActionsAnchorEl(event.currentTarget);
  }, []);

  const handleMoreActionsClose = useCallback(() => {
    setMoreActionsAnchorEl(null);
  }, []);

  const handleExcelExportClick = useCallback(() => {
    handleMoreActionsClose();
    setExcelExportOpen(true);
  }, [handleMoreActionsClose]);

  const handleBulkActionsClick = useCallback(() => {
    handleMoreActionsClose();
    setBulkActionsOpen(true);
  }, [handleMoreActionsClose]);

  const handlePdfExportClick = useCallback(() => {
    handleMoreActionsClose();
    setPdfExportOpen(true);
  }, [handleMoreActionsClose]);

  const hasMoreActions = actionVisibility.excelExport || actionVisibility.bulkActions || actionVisibility.pdfExport;

  const secondaryNavigationActions = useMemo<SecondaryNavigationIconButtonControlConfig[]>(
    () =>
      hasMoreActions
        ? [
          {
            ariaLabel: 'More actions',
            icon: MORE_VERT_ICON,
            onClick: handleMoreActionsOpen,
            type: 'iconButton',
          },
        ]
        : [],
    [handleMoreActionsOpen, hasMoreActions]
  );

  const exportDialogs = (
    <>
      {!isAllFundsSelected && selectedFundId ? (
        <FundExcelExportDialog
          open={excelExportOpen}
          onClose={() => setExcelExportOpen(false)}
          fundId={selectedFundId}
        />
      ) : (
        <FirmExcelExportDialog open={excelExportOpen} onClose={() => setExcelExportOpen(false)} />
      )}
      <BulkActionsDrawer open={bulkActionsOpen} onClose={() => setBulkActionsOpen(false)} />
      <PdfExportConfigDialog open={pdfExportOpen} onClose={() => setPdfExportOpen(false)} />
    </>
  );

  const moreActionsMenu = (
    <PrimaryNavigationMenu
      anchorEl={moreActionsAnchorEl}
      menuMinWidth={0}
      menuWidth="content"
      onClose={handleMoreActionsClose}
      open={Boolean(moreActionsAnchorEl)}>
      {actionVisibility.excelExport && (
        <PrimaryNavigationMenuItem icon={<TableChartOutlined fontSize="inherit" />} onClick={handleExcelExportClick}>
          {EXCEL_EXPORT}
        </PrimaryNavigationMenuItem>
      )}
      {actionVisibility.bulkActions && (
        <PrimaryNavigationMenuItem icon={<PlaylistAddCheckOutlined fontSize="inherit" />} onClick={handleBulkActionsClick}>
          {BULK_ACTIONS}
        </PrimaryNavigationMenuItem>
      )}
      {actionVisibility.pdfExport && (
        <PrimaryNavigationMenuItem icon={<PictureAsPdfOutlined fontSize="inherit" />} onClick={handlePdfExportClick}>
          {PDF_EXPORT}
        </PrimaryNavigationMenuItem>
      )}
    </PrimaryNavigationMenu>
  );

  return {
    secondaryNavigationActions,
    exportDialogs,
    moreActionsMenu,
  };
};
